import { configureStore, combineReducers } from '@reduxjs/toolkit';
import userReducer from './userSlice';
import reportReducer from './reportSlice';
import pricingWizardReducer from './pricingWizardSlice';
import d2cAllocationReducer from './d2cAllocationSlice';
import tatWizardReducer from './tatWizardSlice';
import storage from 'redux-persist/lib/storage';
import { persistReducer, persistStore } from 'redux-persist';
import thunk from 'redux-thunk';
import { commonApiCalls } from "./commonApiCalls"
import { courierPerformanceApi } from "./courierPerformance"
import { wizardApiSlice } from "./wizardApiSlice"
import { tatApiSlice } from "./tatApiSlice"
import { allocationApiSlice } from "./allocationApiSlice"
import { interventionApi } from "./interventionApiSlice"
import { supplyApiSlice } from "./supplyApiSlice"
import { d2cApiSlice } from "./d2cApiSlice"
import { rvpQcApiSlice } from "./rvpQcApiSlice"
import { kciApiSlice } from "./kciApiSlice"
import { freightPnlApi } from "./freightPnlApiSlice"
import { stuckShipmentsApi } from "./stuckShipmentsApiSlice"
import { ptlNabdApi } from "./ptlNabdApiSlice"
import { ptlBillingApi } from "./ptlBillingApiSlice"
import { internalAlertsApi } from "./internalAlertsApiSlice"
import { growthosBrandsApiSlice } from "./growthosBrandsApiSlice"
import growthosBrandsReducer from './growthosBrandsSlice';
import { growthosBudgetApiSlice } from "./growthosBudgetApiSlice"
import { growthosPocsApiSlice } from "./growthosPocsApiSlice"
import { growthosPocAnalyticsApiSlice } from "./growthosPocAnalyticsApiSlice"
import { growthosConnectorsApiSlice } from "./growthosConnectorsApiSlice"
import { growthosRetentionApiSlice } from "./growthosRetentionApiSlice"
import { growthosSmeApiSlice } from "./growthosSmeApiSlice"
import { growthosExecutiveApiSlice } from "./growthosExecutiveApiSlice"
import { growthosDecisionInsightsApiSlice } from "./growthosDecisionInsightsApiSlice"
import { growthosMarketingFunnelApiSlice } from "./growthosMarketingFunnelApiSlice"
import { growthosPipelineFunnelApiSlice } from "./growthosPipelineFunnelApiSlice"
import { growthosHubspotReviewApiSlice } from "./growthosHubspotReviewApiSlice"
import { growthosEmailMarketingApiSlice } from "./growthosEmailMarketingApiSlice"
import { growthosLinkedinAbmApiSlice } from "./growthosLinkedinAbmApiSlice"
import { growthosMarketingCampaignApiSlice } from "./growthosMarketingCampaignApiSlice"
import trackingListSlice from './reducers/trackingListSlice';

// Cleanup of the stored blob before the store boots:
//  - `tatWizard` was persisted before it was blacklisted, and redux-persist v6 only
//    applies the blacklist on WRITE — an already-stored stale (old-shape) snapshot
//    would still rehydrate on the next load, so it's stripped and the wizard starts fresh.
//  - `user.loading`: a sign-in interrupted mid-request (reload, dropped connection) was
//    saved as still in progress, and every later load restored it — the sign-in button
//    stayed on "Signing in…", disabled, until the site's data was cleared. Nothing can
//    be in flight when the page starts, so it starts false.
try {
  const raw = window.localStorage.getItem('persist:root');
  if (raw) {
    const parsed = JSON.parse(raw);
    let changed = false;
    if (parsed.tatWizard !== undefined) {
      delete parsed.tatWizard;
      changed = true;
    }
    if (parsed.user) {
      const user = JSON.parse(parsed.user);
      if (user && user.loading) {
        user.loading = false;
        parsed.user = JSON.stringify(user);
        changed = true;
      }
    }
    if (changed) window.localStorage.setItem('persist:root', JSON.stringify(parsed));
  }
} catch (e) { /* storage unavailable — nothing to clean */ }

const persistConfig = {
  key: 'root',
  storage,
  blacklist: [
    'commonApiCalls',
    'courierPerformanceApi',
    'trackingListSlice',
    'd2cAllocation',
    'wizardApi',
    'tatApi',
    'allocationApi',
    'pricingWizard',
    // Transient setup wizard — must NOT be persisted. Rehydrating an old snapshot
    // whose shape predates new slice keys (e.g. `agreement`) crashes the editor.
    'tatWizard',
    'interventionApi',
    'supplyApi',
    'd2cApi',
    'rvpQcApi',
    'kciApi',
    'freightPnlApi',
    'stuckShipmentsApi',
    'ptlNabdApi',
    'ptlBillingApi',
    'internalAlertsApi',
    'growthosBrandsApi',
    // Local dialog/filter UI state — no cross-session value, avoids stale-shape
    // rehydration bugs as this module evolves (same rationale as tatWizard).
    'growthosBrands',
    'growthosBudgetApi',
    'growthosPocsApi',
    'growthosPocAnalyticsApi',
    'growthosConnectorsApi',
    'growthosRetentionApi',
    'growthosSmeApi',
    'growthosExecutiveApi',
    'growthosDecisionInsightsApi',
    'growthosMarketingFunnelApi',
    'growthosPipelineFunnelApi',
    'growthosHubspotReviewApi',
    'growthosEmailMarketingApi',
    'growthosLinkedinAbmApi',
  ]
}
const reducers = combineReducers({
  [commonApiCalls.reducerPath]: commonApiCalls.reducer,
  [courierPerformanceApi.reducerPath]: courierPerformanceApi.reducer,
  [wizardApiSlice.reducerPath]: wizardApiSlice.reducer,
  [tatApiSlice.reducerPath]: tatApiSlice.reducer,
  [allocationApiSlice.reducerPath]: allocationApiSlice.reducer,
  [interventionApi.reducerPath]: interventionApi.reducer,
  [supplyApiSlice.reducerPath]: supplyApiSlice.reducer,
  [d2cApiSlice.reducerPath]: d2cApiSlice.reducer,
  [rvpQcApiSlice.reducerPath]: rvpQcApiSlice.reducer,
  [kciApiSlice.reducerPath]: kciApiSlice.reducer,
  [freightPnlApi.reducerPath]: freightPnlApi.reducer,
  [stuckShipmentsApi.reducerPath]: stuckShipmentsApi.reducer,
  [ptlNabdApi.reducerPath]: ptlNabdApi.reducer,
  [ptlBillingApi.reducerPath]: ptlBillingApi.reducer,
  [internalAlertsApi.reducerPath]: internalAlertsApi.reducer,
  [growthosBrandsApiSlice.reducerPath]: growthosBrandsApiSlice.reducer,
  [growthosBudgetApiSlice.reducerPath]: growthosBudgetApiSlice.reducer,
  [growthosPocsApiSlice.reducerPath]: growthosPocsApiSlice.reducer,
  [growthosPocAnalyticsApiSlice.reducerPath]: growthosPocAnalyticsApiSlice.reducer,
  [growthosConnectorsApiSlice.reducerPath]: growthosConnectorsApiSlice.reducer,
  [growthosRetentionApiSlice.reducerPath]: growthosRetentionApiSlice.reducer,
  [growthosSmeApiSlice.reducerPath]: growthosSmeApiSlice.reducer,
  [growthosExecutiveApiSlice.reducerPath]: growthosExecutiveApiSlice.reducer,
  [growthosDecisionInsightsApiSlice.reducerPath]: growthosDecisionInsightsApiSlice.reducer,
  [growthosMarketingFunnelApiSlice.reducerPath]: growthosMarketingFunnelApiSlice.reducer,
  [growthosPipelineFunnelApiSlice.reducerPath]: growthosPipelineFunnelApiSlice.reducer,
  [growthosHubspotReviewApiSlice.reducerPath]: growthosHubspotReviewApiSlice.reducer,
  [growthosEmailMarketingApiSlice.reducerPath]: growthosEmailMarketingApiSlice.reducer,
  [growthosLinkedinAbmApiSlice.reducerPath]: growthosLinkedinAbmApiSlice.reducer,
  [growthosMarketingCampaignApiSlice.reducerPath]: growthosMarketingCampaignApiSlice.reducer,
  user: userReducer,
  report: reportReducer,
  pricingWizard: pricingWizardReducer,
  d2cAllocation: d2cAllocationReducer,
  tatWizard: tatWizardReducer,
  growthosBrands: growthosBrandsReducer,
  trackingListSlice: trackingListSlice
});

const rootReducer = (state, action) => {
  if (action.type === 'USER_LOGGED_OUT') {
    state = undefined;
  }

  return reducers(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  devTools: process.env.NODE_ENV !== 'production',
  middleware: [thunk, commonApiCalls.middleware, courierPerformanceApi.middleware, wizardApiSlice.middleware, tatApiSlice.middleware, allocationApiSlice.middleware, interventionApi.middleware, supplyApiSlice.middleware, d2cApiSlice.middleware, rvpQcApiSlice.middleware, kciApiSlice.middleware, freightPnlApi.middleware, stuckShipmentsApi.middleware, ptlNabdApi.middleware, ptlBillingApi.middleware, internalAlertsApi.middleware, growthosBrandsApiSlice.middleware, growthosBudgetApiSlice.middleware, growthosPocsApiSlice.middleware, growthosPocAnalyticsApiSlice.middleware, growthosConnectorsApiSlice.middleware, growthosRetentionApiSlice.middleware, growthosSmeApiSlice.middleware, growthosExecutiveApiSlice.middleware, growthosDecisionInsightsApiSlice.middleware, growthosMarketingFunnelApiSlice.middleware, growthosPipelineFunnelApiSlice.middleware, growthosHubspotReviewApiSlice.middleware, growthosEmailMarketingApiSlice.middleware, growthosLinkedinAbmApiSlice.middleware, growthosMarketingCampaignApiSlice.middleware]
});

export const persistor = persistStore(store);
export default store;